import { useState, useEffect } from 'react';
import { todayInputValue, formatNumber } from '../../../../utils/format';
import { useModalBackHandler } from '../../../../hooks/useModalBackHandler';
import { showToast } from '../../../../components/base/Toast';
import { stockInSupplyItem } from '../../../../store';
import { useAuth } from '../../../../hooks/useAuth';
import type { SupplyItem } from '../../../../types';

interface InStockModalProps {
  item: SupplyItem | null;
  onClose: () => void;
}

export function InStockModal({ item, onClose }: InStockModalProps) {
  useModalBackHandler(true, onClose);
  const { user } = useAuth();
  const [form, setForm] = useState({ soLuong: '', ngay: todayInputValue(), loHang: '', hanSuDung: '', lyDo: '', ghiChu: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item) {
      setForm({ soLuong: '', ngay: todayInputValue(), loHang: item.loHang || '', hanSuDung: item.hanSuDung || '', lyDo: '', ghiChu: '' });
    }
  }, [item]);

  if (!item) return null;

  const handleSave = async () => {
    const soLuong = Number(form.soLuong);
    if (!soLuong || soLuong <= 0) { showToast('Số lượng nhập phải lớn hơn 0', 'warning'); return; }
    if (!form.ngay) { showToast('Vui lòng chọn ngày nhập', 'warning'); return; }
    if (!user?.htxId) return;

    setSaving(true);
    try {
      await stockInSupplyItem(item.id, soLuong, form.lyDo || `Nhập kho ${item.name}`, user.htxId);
      showToast(`Nhập kho thành công: +${soLuong} ${item.unit}`, 'success');
      onClose();
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Nhập kho thất bại', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/40 animate-modal-backdrop" onClick={onClose} />
      <div onClick={(e) => e.stopPropagation()} className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto animate-modal-slide-up">
        <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Nhập kho</h3>
            <p className="text-xs text-slate-400">{item.maVatTu} · {item.name}</p>
          </div>
          <button type="button" onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400"><i className="ri-close-line text-lg" /></button>
        </div>
        <div className="px-6 py-4 space-y-4">
          <div className="bg-slate-50 rounded-xl p-3 flex items-center justify-between">
            <span className="text-sm text-slate-500">Tồn kho hiện tại</span>
            <span className="text-sm font-bold text-slate-900">{formatNumber(item.stock)} {item.unit}</span>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Số lượng nhập <span className="text-red-500">*</span></label>
            <input type="number" value={form.soLuong} onChange={(e) => setForm({ ...form, soLuong: e.target.value })} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" placeholder={`Nhập số lượng (${item.unit})`} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Ngày nhập <span className="text-red-500">*</span></label>
            <input type="date" value={form.ngay} onChange={(e) => setForm({ ...form, ngay: e.target.value })} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Lô hàng</label>
            <input type="text" value={form.loHang} onChange={(e) => setForm({ ...form, loHang: e.target.value })} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" placeholder="Lô hàng mới" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Hạn sử dụng</label>
            <input type="date" value={form.hanSuDung} onChange={(e) => setForm({ ...form, hanSuDung: e.target.value })} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Lý do nhập</label>
            <input type="text" value={form.lyDo} onChange={(e) => setForm({ ...form, lyDo: e.target.value })} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" placeholder="Lý do nhập kho" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Ghi chú</label>
            <textarea value={form.ghiChu} onChange={(e) => setForm({ ...form, ghiChu: e.target.value })} maxLength={500} rows={2} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" placeholder="Ghi chú..." />
          </div>
          {form.soLuong && (
            <div className="bg-agrida-50 rounded-xl p-3 text-sm">
              <p className="text-agrida-700">Tồn kho sau nhập: {formatNumber(item.stock + Number(form.soLuong))} {item.unit}</p>
            </div>
          )}
        </div>
        <div className="sticky bottom-0 bg-white border-t border-slate-100 px-6 py-4 flex gap-3 justify-end">
          <button type="button" onClick={onClose} className="px-4 py-2.5 rounded-xl text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors whitespace-nowrap min-h-[44px]">Hủy</button>
          <button type="button" onClick={handleSave} disabled={saving} className="px-4 py-2.5 rounded-xl text-sm font-medium text-white bg-agrida-700 hover:bg-agrida-800 disabled:opacity-60 transition-colors whitespace-nowrap min-h-[44px]">{saving ? 'Đang lưu...' : 'Nhập kho'}</button>
        </div>
      </div>
    </div>
  );
}